/**
 * SeverityCountChip — the §7.4 severity count chip on the results header
 * (ANA.4; mission 6 WP03 T022; post-plan T5, T13; research §1.18).
 *
 * 28px pill, 13/600, the badge's fills (`severityClasses`, one of the two
 * severity render paths), the count in mono then the word (`11 ERROR`). It
 * filters: `aria-pressed` follows `selected`, and the selected ring is 2px in
 * the severity's 700 shade. `data-severity-chip` marks it as a legend trigger.
 * A disabled chip states its reason (the By record tab), as a tooltip and in
 * its description.
 */
import { useId } from 'react'

// mission 6 WP13: the shared non-empty reason type and its development assertion (N1).
import { assertDisabledReason, type DisabledReason } from '@/components/ui/button'
import { cn } from '@/components/ui/cn'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Mono, count as formatCount } from '@/lib/format'
import { severityClasses, type Severity } from '@/lib/severity'

const SELECTED_RING: Record<Severity, string> = {
  ERROR: '[box-shadow:0_0_0_2px_var(--color-error-700)]',
  WARN: '[box-shadow:0_0_0_2px_var(--color-warning-700)]',
  INFO: '[box-shadow:0_0_0_2px_var(--navy-700)]',
}

type ChipBase = {
  severity: Severity
  count: number
  selected: boolean
  onSelect: () => void
  className?: string
}

export type SeverityCountChipProps = ChipBase &
  // mission 6 WP13: the reason is never empty (WP03 review N1).
  ({ disabled?: false; disabledReason?: undefined } | { disabled: true; disabledReason: DisabledReason })

export function SeverityCountChip({
  severity,
  count,
  selected,
  onSelect,
  className,
  disabled,
  disabledReason,
}: SeverityCountChipProps) {
  const reasonId = useId()
  // mission 6 WP13: a disabled chip states a non-empty reason (N1).
  if (disabled === true) assertDisabledReason('SeverityCountChip', disabledReason)
  const chip = (
    <button
      type="button"
      data-severity-chip={severity}
      aria-pressed={selected}
      disabled={disabled}
      aria-describedby={disabled ? reasonId : undefined}
      onClick={onSelect}
      className={cn(
        'inline-flex h-7 shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 text-body-sm font-semibold transition-shadow duration-[var(--duration-fast)]',
        severityClasses[severity],
        selected && SELECTED_RING[severity],
        'focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]',
        'disabled:cursor-not-allowed disabled:opacity-45',
        className,
      )}
    >
      <Mono>{formatCount(count)}</Mono> {severity}
    </button>
  )
  if (!disabled) return chip
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          className="inline-flex rounded-full focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
        >
          {chip}
          <span id={reasonId} className="sr-only">
            {disabledReason}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{disabledReason}</TooltipContent>
    </Tooltip>
  )
}
